import {Upload, FileText, Bot} from "lucide-react";
import {FC} from "react";
import Stepper from "@/components/Stepper";
import Card from "@/components/Card";
import {Link, useParams} from "react-router-dom";
import DashboardLayout from "@/components/layouts/Dashboard";
import AIButton from "@/components/ui/AIButton";

interface SpecsProps {

}

const Specs: FC<SpecsProps> = () => {
    let {id} = useParams();
    return (
        <DashboardLayout>
            <Card
                borderColor="[#dbe0f6]"
                bgColor="white"
                minHeight="full"
                grow
                stretch
                maxWidth="max-w-7xl"
                mAuto
            >
                <div className="gap-5 w-full grow p-1 xl:p-3 flex flex-col lg:px-0 px-2">
                    <div className="w-[100%] sm:w-[90%] md:w-2/3 min-w-[250px] m-auto pt-3">
                        <Stepper
                            steps={["Project Info", "Specs", "Generate and Refine"]}
                            step={2}
                        />
                    </div>
                    <div className="grid grid-cols-12 gap-6 grow">
                        {/* Upload Specs Section */}
                        <div
                            className="col-span-12 lg:col-span-6 flex flex-col justify-center items-center h-full min-h-[400px] lg:min-h-[800px] gap-4 sm:gap-6 border-r-0 lg:border-r-[1px] lg:border-b-0 border-b-[1px] border-[#8F95B2] relative order-2 lg:order-1 py-6 lg:py-0 opacity-50 pointer-events-none">

                            {/* Coming Soon Badge */}
                            <div className="absolute top-8 left-1/2 -translate-x-1/2 z-20">
                                <div className="bg-gradient-to-r from-gray-600 to-gray-700 text-white px-6 py-2 rounded-full shadow-xl font-bold text-sm tracking-wide">
                                    COMING SOON
                                </div>
                            </div>

                            {/* Upload Button using AIButton */}
                            <div className="w-full sm:w-2/3 min-w-[250px] px-4 sm:px-0 flex justify-center">
                                <AIButton
                                    variant="secondary"
                                    size="lg"
                                    icon={<Upload/>}
                                    showSparkles={true}
                                    showArrow={true}
                                    glowIntensity="medium"
                                    loading={false}
                                    disabled={true}
                                >
                                    Upload Specs
                                </AIButton>
                            </div>

                            {/* Enhanced Info Alert */}
                            <div className="w-full sm:w-2/3 min-w-[250px] px-4 sm:px-0">
                                <div
                                    className="relative overflow-hidden bg-gradient-to-br from-blue-50 to-cyan-50 border-2 border-blue-200 rounded-xl p-4 shadow-lg">
                                    <div
                                        className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-bl from-blue-200/30 to-transparent rounded-full -translate-y-10 translate-x-10"></div>

                                    <div className="relative z-10">
                                        <div className="flex items-center gap-3 mb-3">
                                            <div className="p-2 bg-blue-500 rounded-full">
                                                <FileText className="h-4 w-4 text-white"/>
                                            </div>
                                            <h3 className="font-bold text-blue-800 text-lg">Document Upload</h3>
                                        </div>
                                        <div className="text-blue-700 text-sm leading-relaxed">
                                            Upload your specs in formats like PDF, DOCX, etc.
                                            <br/>
                                            <span
                                                className="font-semibold text-blue-800 bg-blue-100 px-2 py-1 rounded-md mt-2 inline-block">
                        ShredsAI will generate architecture diagram from this document.
                      </span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Enhanced OR Divider */}
                            <div
                                className="bg-gradient-to-r from-purple-500 to-pink-500 text-white absolute top-[-20px] lg:top-1/2 lg:-translate-y-1/2 left-1/2 -translate-x-1/2 lg:right-[-20px] lg:left-auto lg:translate-x-1/2 font-bold tracking-wider text-base w-12 h-12 flex items-center justify-center rounded-full shadow-lg transform rotate-12 hover:rotate-0 transition-transform duration-300">
                                OR
                            </div>
                        </div>

                        {/* AI Creation Section */}
                        <div
                            className="col-span-12 lg:col-span-6 flex flex-col justify-center items-center h-full min-h-[400px] lg:min-h-[800px] gap-4 sm:gap-6 order-1 lg:order-2 py-6 lg:py-0">

                            {/* AI Button using AIButton */}
                            <div data-tour="projects-chose-specs" className="w-full sm:w-2/3 min-w-[250px] px-4 sm:px-0 flex justify-center">
                                <Link to={`/architecture/generate/${id}`}>
                                    <AIButton
                                        variant="primary-cosmic"
                                        size="lg"
                                        icon={<Bot/>}
                                        showSparkles={true}
                                        showArrow={false}
                                        glowIntensity="medium"
                                        loading={false}
                                    >
                                        Use AI to Create Specs
                                    </AIButton>
                                </Link>
                            </div>

                            {/* Enhanced Info Alert */}
                            <div className="w-full sm:w-2/3 min-w-[250px] px-4 sm:px-0">
                                <div
                                    className="relative overflow-hidden bg-gradient-to-br from-purple-50 to-pink-50 border-2 border-purple-200 rounded-xl p-4 shadow-lg">
                                    <div
                                        className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-bl from-purple-200/30 to-transparent rounded-full -translate-y-10 translate-x-10"></div>

                                    <div className="relative z-10">
                                        <div className="flex items-center gap-3 mb-3">
                                            <div
                                                className="p-2 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full">
                                                <Bot className="h-4 w-4 text-white"/>
                                            </div>
                                            <h3 className="font-bold text-purple-800 text-lg">AI-Powered Creation</h3>
                                        </div>
                                        <div className="text-purple-700 text-sm leading-relaxed">
                                            Write the most accurate description of your project. AI will generate the
                                            best specs possible.
                                            <br/>
                                            <span
                                                className="font-semibold text-purple-800 bg-purple-100 px-2 py-1 rounded-md mt-2 inline-block">
                        You can later ask Shreds AI to modify and refine generated specs.
                      </span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </Card>
        </DashboardLayout>
    );
};

export default Specs;